"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";

/**
 * Badge "🟢 En ligne maintenant" + bouton "Consulter maintenant" affiché sur
 * la fiche médecin sahha.ma. Polling 12s pour rester à jour avec le statut
 * du médecin (qui peut passer offline à tout moment).
 *
 * Click "Consulter maintenant" :
 *  1. Si user pas connecté → redirect /connexion?returnTo=...
 *  2. Sinon → redirect /teleconsult/start?slug=... (page paiement)
 *
 * Cache 0 : on veut vraiment refresh régulier, pas du SWR.
 */

type Status = {
  isLive: boolean;
  mode?: string;
  estimatedWaitMin?: number;
  livePriceMad?: number | null;
};

type Props = {
  physicianSlug: string;
  isAuthenticated: boolean;
};

export function TeleconsultLiveBadge({ physicianSlug, isAuthenticated }: Props) {
  const router = useRouter();
  const t = useTranslations("physician.teleconsultLive");
  const [status, setStatus] = useState<Status | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await fetch(`/api/physicians/${encodeURIComponent(physicianSlug)}/online-status`, { cache: "no-store" });
        const data = await res.json();
        if (!cancelled) setStatus(data);
      } catch {/* noop — polling re-essaiera */}
    }
    load();
    const id = setInterval(load, 12_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [physicianSlug]);

  if (!status?.isLive) return null;

  const wait = status.estimatedWaitMin ?? 0;
  const waitLabel = wait === 0 ? t("waitImmediate") : t("waitDelay", { minutes: wait });
  const price = status.livePriceMad;

  function handleClick() {
    if (!isAuthenticated) {
      router.push(`/connexion?returnTo=${encodeURIComponent(`/teleconsult/start?slug=${physicianSlug}`)}`);
      return;
    }
    router.push(`/teleconsult/start?slug=${physicianSlug}`);
  }

  return (
    <div className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
          <span className="text-lg">📹</span>
          <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 animate-pulse rounded-full bg-emerald-400 ring-2 ring-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-bold uppercase tracking-wider text-emerald-700">{t("kicker")}</p>
          <p className="text-[14px] font-semibold text-slate-900">{t("labelWait", { wait: waitLabel })}</p>
        </div>
      </div>

      <button
        type="button"
        onClick={handleClick}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-700 px-5 py-3 text-[14px] font-bold text-white shadow-md transition-all hover:from-emerald-500 hover:to-emerald-600 active:scale-[0.98]"
      >
        {t("consultNow")}
        {price && <span className="opacity-80 font-semibold">· {price} MAD</span>}
      </button>

      <p className="mt-2 text-[11.5px] text-slate-500 text-center">
        {t("footer")}
      </p>
    </div>
  );
}
